import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";
import {useMediaQuery} from "@khan/util-media-query";

interface Props {
    // The icon to display (e.g., GoogleIcon, FacebookIcon, etc.)
    icon: React.ReactNode;
    // The provider label (e.g., "Google", "Facebook", "Email")
    label: string;
    // Optional content to display in the middle section (e.g., email address, status)
    content?: React.ReactNode;
    // The action button or element to display on the right
    action: React.ReactNode;
}

/**
 * A responsive linked account row that hides labels and email content below 500px width
 */
export const ResponsiveLinkedAccountRow: React.FC<Props> = ({
    icon,
    label,
    content,
    action,
}) => {
    const isVerySmallScreen = useMediaQuery("(max-width: 500px)");

    return (
        <View style={styles.row}>
            <View style={styles.leftSection}>
                <View style={styles.iconLabelContainer}>
                    <View style={styles.iconContainer}>{icon}</View>
                    <View style={{width: sizing.size_120}} />
                    {!isVerySmallScreen && (
                        <LabelMedium style={styles.label}>{label}</LabelMedium>
                    )}
                </View>
                {!isVerySmallScreen && content && (
                    <LabelMedium style={styles.contentText}>
                        {content}
                    </LabelMedium>
                )}
            </View>
            <View style={styles.rightSection}>{action}</View>
        </View>
    );
};

const styles = StyleSheet.create({
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: sizing.size_120,
    },
    leftSection: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        minInlineSize: 0,
        paddingInlineEnd: sizing.size_120,
    },
    iconLabelContainer: {
        flexDirection: "row",
        alignItems: "center",
        width: 160,
        flexShrink: 0,
        [mediaQueries.smOrSmaller]: {
            width: 120,
        },
    },
    iconContainer: {
        width: sizing.size_280,
        height: sizing.size_280,
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    rightSection: {
        alignItems: "center",
        justifyContent: "flex-end",
        flexShrink: 0,
        minInlineSize: "fit-content",
    },
    label: {
        color: semanticColor.core.foreground.neutral.strong,
    },
    contentText: {
        color: semanticColor.core.foreground.neutral.strong,
        wordBreak: "break-word",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
});

export default ResponsiveLinkedAccountRow;
